/** `/draws` — every draw of the tenant with its §9.5 state; opens the control room of one draw. */
import { useQuery } from '@tanstack/react-query';
import type { DrawAdminDto } from '@windrop/contracts';
import { Button, Skeleton, useIntl } from '@windrop/ui';
import { Link } from 'react-router';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { isApiError } from '../../lib/api/client';
import { drawsApi } from './api';

export function DrawsListPage() {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['draws', 'list'],
    queryFn: () => drawsApi.list(),
    refetchInterval: 30_000,
  });
  const columns: Column<DrawAdminDto>[] = [
    {
      id: 'lottery',
      header: 'Lottery',
      cell: (d) => <Link to={`/draws/${d.drawId}`}>{d.title}</Link>,
      sortValue: (d) => d.title,
      filterValue: (d) => `${d.title} ${d.slug}`,
    },
    {
      id: 'mechanic',
      header: 'Mechanic',
      cell: (d) => humanizeStatus(d.mechanic),
      sortValue: (d) => d.mechanic,
    },
    {
      id: 'status',
      header: 'Draw status',
      cell: (d) => <StatusBadge kind="DrawStatus" value={d.status} />,
      sortValue: (d) => d.status,
    },
    {
      id: 'lotteryStatus',
      header: 'Lottery',
      cell: (d) => humanizeStatus(d.lotteryStatus),
      sortValue: (d) => d.lotteryStatus,
    },
    {
      id: 'when',
      header: 'Draw / close',
      cell: (d) =>
        d.drawAt
          ? intl.formatDateTime(d.drawAt)
          : d.closesAt
            ? `closes ${intl.formatDateTime(d.closesAt)}`
            : '—',
      sortValue: (d) => d.drawAt ?? d.closesAt ?? '',
    },
    {
      id: 'winners',
      header: 'Winners',
      cell: (d) => (
        <span className="wd-tabular">
          {d.counts.winners}
          {d.counts.sponsorSamples ? ` (+${d.counts.sponsorSamples} samples)` : ''}
        </span>
      ),
      sortValue: (d) => d.counts.winners,
      align: 'end',
    },
    {
      id: 'highValue',
      header: 'Control',
      cell: (d) =>
        d.highValue ? (
          <ToneBadge tone="warning">Two operators</ToneBadge>
        ) : (
          <ToneBadge tone="neutral">Single</ToneBadge>
        ),
    },
    {
      id: 'open',
      header: '',
      cell: (d) => (
        <Button href={`/draws/${d.drawId}`} variant="secondary">
          Open room
        </Button>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Draw control room"
        description="Pool reconciliation, seed and snapshot status, inventory checks, two-person controls, reveal rehearsal, static fallback and fairness export (§16.2)."
        breadcrumb={[{ label: 'Draw control room' }]}
      />
      {query.isPending ? (
        <Skeleton shape="block" height={240} label="Loading draws" />
      ) : query.isError ? (
        <ErrorState
          title="Draws are not available"
          body="The outcome API did not answer. Retry, or open a lottery's Draw tab."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}
        />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          title="No draws yet"
          body="A seed commitment is created when a lottery is published; it appears here with its gates."
          action={<Button href="/lotteries">Open lotteries</Button>}
        />
      ) : (
        <DataTable
          caption="Draws"
          columns={columns}
          rows={query.data.items}
          rowKey={(d) => d.drawId}
          filterable
          initialSort={{ columnId: 'when', direction: 'desc' }}
        />
      )}
    </>
  );
}
